import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import { useNavigate } from "react-router-dom";
import {
  usePreviewAssignmentBillingMutation,
  useGetStudentBillingCurrentQuery,
} from "../../../Api/attoDeskApi";
import SwitchStudentBillingModal from "../../Student/Component/SwitchStudentBillingModal";
import type {
  BillingTypeV2,
  StudentBillingCurrent,
} from "../../Student/Component/StudentBillingTypeSection";

/**
 * Assignment-time billing panel for the v2 engine. Given a (student, class,
 * date) triple, it calls /billing/preview-assignment and renders:
 *
 *   • Monthly student — "Not charged separately" banner + read-only class fee
 *     tagged "Reference only". Provides "Switch to Class-Wise Billing" action.
 *   • Class-Wise student — Class fee + tax + start date + "Will be used for
 *     future invoices" banner. Provides "Switch to Monthly Billing" action.
 *   • Class-Wise + no class fee — Blocking warning and disables the caller's
 *     confirm button via onBlockingChange(true).
 *
 * The panel never asks the admin to type a fee. Fee comes from the class.
 *
 * onBlockingChange is called with `true` while assignment should be blocked
 * (missing class fee for a class-wise student). Parents disable their commit
 * button while any panel reports blocking.
 */

const money = (v: number | null | undefined): string =>
  v === null || v === undefined || Number.isNaN(v)
    ? "—"
    : `$${Number(v).toFixed(2)}`;

export interface AssignmentBillingV2PanelProps {
  studentId: number | string;
  classId: number;
  classLabel?: string;
  billingStartDate?: string | null; // YYYY-MM-DD
  onBlockingChange?: (blocking: boolean) => void;
  compact?: boolean;
}

const AssignmentBillingV2Panel: React.FC<AssignmentBillingV2PanelProps> = ({
  studentId,
  classId,
  classLabel,
  billingStartDate,
  onBlockingChange,
  compact,
}) => {
  const navigate = useNavigate();

  const [preview, { data: previewResp, isLoading: isPreviewing, error: previewError }] =
    usePreviewAssignmentBillingMutation();

  const currentBillingQuery = useGetStudentBillingCurrentQuery(
    studentId ? Number(studentId) : 0,
    { skip: !studentId }
  );

  const [switchOpen, setSwitchOpen] = useState(false);

  const onDate =
    billingStartDate ||
    new Date().toISOString().slice(0, 10);

  useEffect(() => {
    if (!studentId || !classId) return;
    preview({
      student_id: Number(studentId),
      class_id: Number(classId),
      on_date: onDate,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId, classId, onDate]);

  const data = (previewResp as any)?.data as
    | {
        billing_type: BillingTypeV2 | null;
        monthly_amount: number | null;
        tax_applicable: boolean;
        class_fee: number | null;
        class_fee_source: "history" | "class_row" | "unset";
        class_tax_status: "exempt" | "applicable" | null;
        will_be_billed: number | null;
        message: string;
      }
    | undefined;

  const currentBilling: StudentBillingCurrent | null = (currentBillingQuery.data as any)?.data
    ? {
        billing_type: (currentBillingQuery.data as any).data.billing_type ?? null,
        monthly_amount:
          (currentBillingQuery.data as any).data.monthly_amount !== null
            ? Number((currentBillingQuery.data as any).data.monthly_amount)
            : null,
        tax_applicable: !!(currentBillingQuery.data as any).data.tax_applicable,
        effective_from: (currentBillingQuery.data as any).data.effective_from ?? null,
      }
    : null;

  const isMonthly = data?.billing_type === "monthly";
  const isClassWise = data?.billing_type === "class_wise";
  const feeMissing = isClassWise && (data?.class_fee === null || data?.class_fee === undefined);

  useEffect(() => {
    onBlockingChange?.(feeMissing);
  }, [feeMissing, onBlockingChange]);

  if (isPreviewing && !data) {
    return (
      <Box
        sx={{
          p: 2,
          borderRadius: 2,
          border: (t) => `1px dashed ${t.palette.divider}`,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">
          Loading billing preview…
        </Typography>
      </Box>
    );
  }

  if (previewError && !data) {
    const errMsg =
      (previewError as any)?.data?.message ||
      (previewError as any)?.message ||
      "Could not load billing preview.";
    return (
      <Alert severity="error">
        {errMsg}
      </Alert>
    );
  }

  if (!data) return null;

  return (
    <Box
      sx={{
        borderRadius: 2,
        border: (t) => `1px solid ${t.palette.divider}`,
        p: compact ? 1.5 : 2,
        bgcolor: (t) =>
          isMonthly
            ? alpha(t.palette.warning.main, 0.04)
            : isClassWise
              ? alpha(t.palette.info.main, 0.04)
              : alpha(t.palette.grey[500], 0.04),
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <ReceiptLongOutlinedIcon fontSize="small" />
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Billing
          {classLabel ? ` — ${classLabel}` : ""}
        </Typography>
        {data.billing_type && (
          <Chip
            size="small"
            label={isMonthly ? "Monthly Billing" : "Class-Wise Billing"}
            color={isMonthly ? "warning" : "info"}
            variant="filled"
          />
        )}
      </Box>

      <Stack spacing={0.5} sx={{ mb: 1 }}>
        <Row label="Student Billing Type" value={isMonthly ? "Monthly Billing" : isClassWise ? "Class-Wise Billing" : "Not set"} />
        {isMonthly && (
          <Row label="Monthly Amount" value={money(data.monthly_amount)} />
        )}
        <Row
          label="Class Fee"
          value={
            data.class_fee === null
              ? "Not configured"
              : money(data.class_fee)
          }
          tag={
            isMonthly
              ? "Reference only — not billed separately"
              : data.class_fee_source === "history"
                ? undefined
                : data.class_fee_source === "class_row"
                  ? "From class default"
                  : undefined
          }
        />
        {isClassWise && data.class_tax_status && (
          <Row
            label="Tax"
            value={data.class_tax_status === "applicable" ? "Applicable" : "Exempt"}
          />
        )}
        {isClassWise && billingStartDate && (
          <Row label="Billing Start Date" value={billingStartDate} />
        )}
        <Row
          label="Billing Result"
          value={
            isMonthly
              ? "Not charged separately"
              : isClassWise
                ? feeMissing
                  ? "Blocked — set the class fee first"
                  : "This class fee will be used for future invoices"
                : "Billing type not set on student"
          }
          strong
        />
      </Stack>

      {isMonthly && (
        <Alert
          severity="warning"
          icon={<LockOutlinedIcon />}
          sx={{ mb: 1 }}
        >
          This student uses monthly billing. The monthly amount overrides the
          class fee. You do not need to enter a fee here.
        </Alert>
      )}
      {isClassWise && !feeMissing && (
        <Alert
          severity="info"
          icon={<CheckCircleOutlinedIcon />}
          sx={{ mb: 1 }}
        >
          This class fee is configured on the class and will be used
          automatically. You do not need to enter it again.
        </Alert>
      )}
      {feeMissing && (
        <>
          <Alert
            severity="warning"
            icon={<WarningAmberOutlinedIcon />}
            sx={{ mb: 1 }}
          >
            This class does not have an active fee. Add a class fee before
            assigning it for class-wise billing.
          </Alert>
          <Button
            size="small"
            variant="outlined"
            onClick={() => navigate(`/classes/edit/${classId}`)}
          >
            Open Class Fee Settings
          </Button>
        </>
      )}
      {!data.billing_type && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {data.message}
        </Alert>
      )}

      <Divider sx={{ my: 1 }} />
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          size="small"
          variant="text"
          onClick={() => setSwitchOpen(true)}
        >
          {isMonthly ? "Switch to Class-Wise Billing" : "Switch to Monthly Billing"}
        </Button>
      </Box>

      <SwitchStudentBillingModal
        open={switchOpen}
        onClose={() => setSwitchOpen(false)}
        studentId={studentId}
        current={currentBilling}
        proposed={
          data.billing_type
            ? {
                billing_type_v2: data.billing_type === "monthly" ? "class_wise" : "monthly",
                monthly_amount: data.monthly_amount,
                billing_effective_from: onDate,
                tax_applicable: !!data.tax_applicable,
              }
            : null
        }
        onSuccess={() => {
          currentBillingQuery.refetch();
          preview({
            student_id: Number(studentId),
            class_id: Number(classId),
            on_date: onDate,
          });
        }}
      />
    </Box>
  );
};

const Row: React.FC<{ label: string; value: string; tag?: string; strong?: boolean }> = ({
  label,
  value,
  tag,
  strong,
}) => (
  <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap" }}>
    <Typography
      variant="caption"
      sx={{ color: "text.secondary", minWidth: 150, textTransform: "uppercase", letterSpacing: 0.3, fontWeight: 700 }}
    >
      {label}
    </Typography>
    <Typography sx={{ fontSize: 13, fontWeight: strong ? 800 : 600 }}>
      {value}
    </Typography>
    {tag && (
      <Typography variant="caption" sx={{ color: "text.secondary", fontStyle: "italic" }}>
        ({tag})
      </Typography>
    )}
  </Box>
);

export default AssignmentBillingV2Panel;
